import { ArrowLeft, Briefcase, Check, ChevronRight, Play, Square } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../../lib/api.ts';
import type { Bag } from '../../lib/equipment.ts';
import { listBags } from '../../lib/equipment.ts';
import { MODES } from '../../lib/modes.ts';
import type { RunItem, RunState } from '../../lib/planning.ts';
import { etapeFaite, finishRun, getCurrentRun } from '../../lib/planning.ts';
import { startSession } from '../../lib/training.ts';
import { describe } from './WorkoutComposer.tsx';

/* Le déroulé d'une séance, sur le tapis.
 *
 * C'est l'écran auquel on revient entre deux exercices, et le seul endroit du
 * parcours où le joueur a les mains libres. Tout y est donc dit d'un coup
 * d'œil : ce qui est fait et comment ça s'est passé, où on en est, ce qui
 * reste.
 *
 * ── Un seul geste pour lancer ───────────────────────────────────────────────
 *
 * Démarrer n'ouvre AUCUN écran de réglage. La date, c'est maintenant ; le sac,
 * c'est le sac actif ; le mémo, c'est le nom de l'exercice. Ces trois
 * questions ont un sens quand on ouvre l'application au calme pour saisir un
 * exercice isolé — elles n'en ont aucun quand on suit un plan écrit la veille,
 * un club dans la main. On les pose donc là-bas et pas ici.
 */

const itemLabel = (it: RunItem): string =>
    (it.isPutting ? 'Putting' : null)
    ?? it.exerciseName ?? MODES.find((m) => m.mode === it.mode)?.label ?? 'Exercice';

/** Message d'echec au demarrage.
 *
 *  Le 409 est le garde-fou « un seul exercice ouvert a la fois ». Il est
 *  legitime, mais l'annoncer sans dire ou se trouve cet exercice laissait le
 *  joueur devant un mur : on le conduit donc la-bas, ou il pourra le finir ou
 *  l'abandonner. */
function messageDemarrage(e: unknown): string {
    if (e instanceof ApiError && e.status === 409) {
        return "Un exercice est deja en cours. Ouvrez-le depuis Entrainement pour le finir "
            + "ou l'abandonner, puis revenez ici.";
    }
    return e instanceof ApiError ? e.message : 'Demarrage impossible';
}

export function RunScreen() {
    const navigate = useNavigate();
    const [state, setState] = useState<RunState | null>(null);
    const [bags, setBags] = useState<Bag[]>([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [confirmAbort, setConfirmAbort] = useState(false);

    const load = useCallback(async () => {
        try {
            const [r, b] = await Promise.all([getCurrentRun(), listBags()]);
            setState(r);
            setBags(b);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Chargement impossible');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { void load(); }, [load]);

    const bag = bags.find((b) => b.isActive && b.clubs.length > 0)
        ?? bags.find((b) => b.clubs.length > 0);

    /** Lance l'étape et enchaîne directement sur la saisie.
     *
     *  Deux appels et une navigation, sans écran intermédiaire : c'est le
     *  chemin le plus court entre « je pose mon seau » et « je tape ». */
    const start = async (it: RunItem): Promise<void> => {
        // Le sac n'est exigé que pour le practice : une étape de putting se
        // joue au putter, choisi dans le matériel et non dans un sac. Exiger
        // un sac ici bloquait une séance de putting pur.
        if (!state?.run) return;
        // Ni sac ni club pour une étape de parcours : elle ne se joue pas ici.
        if (!bag && !it.isPutting && it.kind !== 'course') return;
        // Étape déjà entamée — écran verrouillé, application fermée : on
        // rejoint la saisie sans rien créer. En ouvrir une seconde ferait deux
        // exercices là où le joueur n'en a fait qu'un.
        if (it.sessionStatus === 'in_progress') {
            void navigate(it.isPutting ? `/putting/${it.sessionId}` : '/entrainement/exercices');
            return;
        }
        // L'étape de parcours ne crée pas de séance non plus : elle se juge sur
        // une carte déjà rendue, et son écran sert à désigner laquelle.
        // Démarrer ici ouvrirait un pavé de saisie que rien ne remplirait.
        if (it.kind === 'course') {
            void navigate(`/entrainement/seance-en-cours/parcours/${it.position}`);
            return;
        }
        // Le putting a son propre parcours : on ne crée rien ici, on l'ouvre en
        // lui passant l'étape qu'il couvre. Il faut y choisir un putter et,
        // le cas échéant, inviter des partenaires — des questions que ce
        // déroulé n'a pas à poser à leur place.
        if (it.isPutting) {
            void navigate(`/putting/nouvelle?run=${state.run.id}&pos=${it.position}`);
            return;
        }
        setBusy(true); setError(null);
        try {
            await startSession({
                mode: it.mode ?? 'free',
                bagId: bag!.id,
                notes: it.exerciseName ?? null,
                customExerciseId: it.exerciseId,
                runId: state.run.id,
                runPosition: it.position,
            }, {
                /* Ce que le serveur recopierait dans sa réponse, et que le
                   déroulé a déjà sous la main : sans lui, l'écran de saisie
                   s'ouvre sans la consigne ni le barème de l'atelier, le temps
                   que la file se vide — c'est-à-dire précisément pendant qu'on
                   tape les premières balles. */
                bagName: bag!.name,
                exercice: it.exerciseId ? {
                    name: it.exerciseName ?? '',
                    description: it.exerciseDescription,
                    successLabel: it.successLabel,
                    criteria: it.criteria,
                    ballsPerRound: it.ballsPerRound,
                    rounds: it.rounds,
                    clubTemplateIds: it.clubTemplateIds,
                } : null,
            });
            void navigate('/entrainement/exercices');
        } catch (e) {
            setError(messageDemarrage(e));
            setBusy(false);
        }
    };

    const onFinish = async (status: 'completed' | 'aborted'): Promise<void> => {
        if (!state?.run) return;
        // Retenu AVANT la clôture : `finishRun` avec 'aborted' supprime la
        // séance, et lire son identifiant après coup n'aurait plus de sens.
        const runId = state.run.id;
        setBusy(true);
        try {
            await finishRun(runId, status);
            // Terminée, on montre ce qu'elle a donné ; abandonnée, il n'y a
            // rien à montrer — le bilan serait un écran vide sur des exercices
            // qu'on vient justement d'effacer.
            void navigate(status === 'completed'
                ? `/entrainement/bilan/${runId}`
                : '/entrainement');
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Clôture impossible');
            setBusy(false);
        }
    };

    if (loading) {
        return <div className="flex justify-center py-16"><span className="loading loading-ring loading-lg text-primary" /></div>;
    }

    // Plus de séance ouverte : on ne laisse pas un écran vide, on ramène là où
    // il y a quelque chose à faire.
    if (!state?.run) {
        return (
            <div className="space-y-4 py-2">
                <h1 className="text-2xl font-bold tracking-tight">Aucune séance en cours</h1>
                <p className="text-sm opacity-70">
                    Choisissez une séance à dérouler, ou lancez un exercice seul.
                </p>
                <Link to="/entrainement" className="btn btn-primary btn-block tap-target">
                    Aller à l'entraînement
                </Link>
            </div>
        );
    }

    const items = state.items;
    const done = items.filter((i) => i.sessionStatus === 'completed').length;
    // L'étape courante : la première qui n'est pas terminée. Une reprise après
    // un téléphone verrouillé retombe donc naturellement au bon endroit, sans
    // qu'on ait à mémoriser quoi que ce soit côté client.
    const current = items.find((i) => !etapeFaite(i)) ?? null;
    const finished = current === null;
    /** La séance se note-t-elle en points ? Le plan seul le dit. */
    const enPoints = state.run.pointsTarget !== null;

    return (
        <div className="space-y-4 py-2">
            <header>
                <Link to="/entrainement" className="btn btn-ghost btn-sm mb-2 gap-2">
                    <ArrowLeft size={16} /> Entraînement
                </Link>
                <h1 className="text-2xl font-bold tracking-tight">{state.run.name}</h1>
                <p className="mt-1 text-sm opacity-70">
                    {done} sur {items.length} — {finished ? 'séance terminée' : `en cours : ${itemLabel(current)}`}
                </p>
                <progress className="progress progress-primary mt-2 w-full"
                    value={done} max={Math.max(1, items.length)} />
            </header>

            {error && <div role="alert" className="alert alert-error text-sm"><span>{error}</span></div>}

            {!bag && (
                <div className="rounded-box border border-warning/40 bg-warning/10 p-4">
                    <p className="font-semibold">Aucun sac utilisable</p>
                    <p className="mt-1 text-sm opacity-80">
                        Placez au moins un club dans un sac pour pouvoir enregistrer des coups.
                    </p>
                    <Link to="/materiel" className="btn btn-warning mt-3 gap-2 tap-target">
                        <Briefcase size={18} /> Aller à Mon matériel
                    </Link>
                </div>
            )}

            <ol className="space-y-2">
                {items.map((it) => {
                    const isDone = it.sessionStatus === 'completed';
                    const isCurrent = current !== null && it.position === current.position;
                    // Trois états, trois traitements. Le courant est le seul
                    // encadré : sur un écran lu à bout de bras, en plein soleil,
                    // c'est le contour qui se voit avant la couleur.
                    return (
                        <li key={it.position}
                            className={`rounded-box p-4 ${
                                isCurrent ? 'border-2 border-primary bg-primary/10'
                                    : isDone ? 'bg-base-200' : 'bg-base-200 opacity-70'
                            }`}>
                            <div className="flex items-start gap-3">
                                <span className={`flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                                    isDone ? 'bg-success text-success-content'
                                        : isCurrent ? 'bg-primary text-primary-content'
                                            : 'bg-base-300'
                                }`}>
                                    {isDone ? <Check size={18} /> : it.position}
                                </span>
                                <span className="min-w-0 flex-1">
                                    <span className="block font-bold">{itemLabel(it)}</span>
                                    {/* La consigne, sur l'étape courante seulement :
                                        ailleurs elle allongerait la liste sans
                                        servir, et c'est la liste qu'on vient lire. */}
                                    {isCurrent && it.exerciseDescription && (
                                        <span className="mt-1 block text-sm opacity-80">{it.exerciseDescription}</span>
                                    )}
                                    <span className="mt-0.5 block text-xs opacity-60">
                                        {it.kind === 'course'
                                            ? `Sur un parcours, ${it.courseMinHoles ?? 3} trous au moins`
                                            : it.isPutting ? 'Sur le green, seul ou à plusieurs'
                                                : it.mode ? 'Mode intégré'
                                                    : describe(it.ballsPerRound, it.rounds)}
                                        {/* Le verdict de l'étape de parcours : ni
                                            balles ni taux, un oui ou un non. */}
                                        {it.coursePassed != null
                                            && ` · ${it.coursePassed ? 'validée' : 'non validée'}`}
                                        {/* Le putting ne compte pas de balles
                                            centrées : on y compte des putts, et
                                            afficher « 0 % » serait faux. */}
                                        {isDone && it.isPutting
                                            && ` · ${it.shots ?? 0} trou${(it.shots ?? 0) > 1 ? 's' : ''} joué${(it.shots ?? 0) > 1 ? 's' : ''}`}
                                        {/* Des POINTS quand la séance en compte. Le taux
                                            n'est pas la note de l'atelier, et le
                                            déroulé est l'endroit où l'on fait ses comptes
                                            entre deux ateliers — pas seulement au bilan. */}
                                        {isDone && !it.isPutting && enPoints && it.pointsMax != null
                                            && ` · ${it.center ?? 0} point${(it.center ?? 0) > 1 ? 's' : ''} sur ${it.pointsMax}`}
                                        {/* Une étape sans barème dans un plan noté en
                                            points — un mode intégré, par exemple —
                                            retombe sur le taux. Sans ce repli, elle
                                            perdait son résultat et n'affichait plus
                                            qu'une coche. */}
                                        {isDone && !it.isPutting && (!enPoints || it.pointsMax == null)
                                            && it.successRate != null
                                            && ` · ${Number(it.successRate).toFixed(0)} % sur ${it.shots} balle${(it.shots ?? 0) > 1 ? 's' : ''}`}
                                    </span>
                                </span>
                                {isDone && it.sessionId && (
                                    <Link to={it.isPutting
                                        ? `/putting/${it.sessionId}`
                                        : `/entrainement/${it.sessionId}`}
                                        aria-label={`Détail de ${itemLabel(it)}`}
                                        className="btn btn-ghost btn-sm btn-square shrink-0">
                                        <ChevronRight size={18} />
                                    </Link>
                                )}
                            </div>

                            {isCurrent && (
                                <button type="button"
                                    disabled={busy || (!bag && !it.isPutting && it.kind !== 'course')}
                                    onClick={() => void start(it)}
                                    className="btn btn-primary btn-block mt-3 h-14 gap-2 text-lg font-bold tap-target">
                                    <Play size={22} />
                                    {it.sessionStatus === 'in_progress' ? 'Reprendre' : 'Démarrer'}
                                </button>
                            )}
                        </li>
                    );
                })}
            </ol>

            {finished ? (
                <button type="button" disabled={busy} onClick={() => void onFinish('completed')}
                    className="btn btn-success btn-block h-14 gap-2 text-lg font-bold tap-target">
                    <Check size={22} /> Clore la séance
                </button>
            ) : (
                /* Deux sorties, et elles ne font pas la même chose.
                 *
                 * Il n'y en avait qu'une, « Abandonner », qui EFFACE. Or on
                 * s'arrête le plus souvent parce que le seau est vide ou qu'il
                 * pleut, pas parce qu'on renie ce qu'on vient de faire — et
                 * perdre trois exercices déjà joués pour ça est inacceptable.
                 *
                 * Arrêter garde tout et clôt l'entraînement là où il en est ;
                 * abandonner reste possible, en dessous, pour la séance ouverte
                 * par erreur. Chaque bouton dit ce qu'il advient du travail
                 * fait, parce que c'est la seule question qu'on se pose. */
                <div className="space-y-2">
                    <button type="button" disabled={busy} onClick={() => void onFinish('completed')}
                        className="btn btn-outline btn-block h-14 gap-2 text-base font-bold tap-target">
                        <Square size={18} /> Arrêter ici et garder
                    </button>
                    <p className="text-center text-xs opacity-70">
                        {done === 0
                            ? "Aucun exercice terminé pour l'instant."
                            : `Vos ${done} exercice${done > 1 ? 's' : ''} terminé${done > 1 ? 's' : ''} sont conservé${done > 1 ? 's' : ''}, avec leur bilan.`}
                    </p>
                    <button type="button" disabled={busy} onClick={() => setConfirmAbort(true)}
                        className="btn btn-ghost btn-sm btn-block text-error">
                        Abandonner et tout effacer
                    </button>
                </div>
            )}

            {confirmAbort && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                    <div className="w-full max-w-sm rounded-box bg-base-200 p-5">
                        <h2 className="text-lg font-bold">Abandonner « {state.run.name} » ?</h2>
                        <p className="mt-2 text-sm opacity-80">
                            {done === 0
                                ? "Rien n'a encore été enregistré : il n'y a rien à perdre."
                                : `Les ${done} exercice${done > 1 ? 's' : ''} déjà terminé${done > 1 ? 's' : ''} seront effacé${done > 1 ? 's' : ''}. Pour les garder, utilisez « Arrêter ici et garder ».`}
                        </p>
                        <div className="mt-4 space-y-2">
                            <button type="button" disabled={busy}
                                onClick={() => void onFinish('aborted')}
                                className="btn btn-error btn-block tap-target">
                                Abandonner
                            </button>
                            <button type="button" className="btn btn-ghost btn-block"
                                onClick={() => setConfirmAbort(false)}>
                                Revenir à la séance
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
